/**
 * Centralized strings for the Community Marketplace feature
 * All user-facing text should be defined here for easy localization and maintenance
 */

export const marketplaceStrings = {
  // ============================================================================
  // Common / Shared
  // ============================================================================
  common: {
    marketplace: 'Marketplace',
    myProfile: 'My profile',
    back: 'Back',
    next: 'Next',
    submit: 'Submit',
    update: 'Update',
    cancel: 'Cancel',
    close: 'Close',
    done: 'Done',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    noCancel: 'No, cancel',
    proceed: 'Proceed',
    unknownUser: 'Unknown User',
    unknownItem: 'Unknown Item',
  },

  // ============================================================================
  // Home / Hero Section
  // ============================================================================
  home: {
    title: 'Community marketplace',
    description: 'Safe, local, and easy - right within your community.',
    postItem: 'Post an item',
    noListingsYet: 'No listings yet',
    beFirstToPost: 'Be the first to post an item!',
  },

  // ============================================================================
  // Post Item Flow
  // ============================================================================
  postItem: {
    discardChanges: 'Discard Changes?',
    discardListingMessage: 'Are you sure you want to discard your listing?',
    discardEditsMessage: 'Are you sure you want to discard your changes?',
    discard: 'Discard',

    // Step 1: Item Details
    whatAreSelling: 'What are you selling?',
    briefTitle: 'Brief title',
    descriptionOptional: 'Description (optionally)',
    titleRequired: 'Title Required',
    pleaseEnterTitle: 'Please enter a title for your item.',

    // Step 2: Media
    addPhotos: 'Add photos',
    addPhotosSubtitle: 'Upload up to 10 photos',
    tapToAdd: 'Tap to add photos',
    photoLimit: 'You can add up to 10 photos',

    // Step 3: Price & Condition
    details: 'Details',
    setPrice: 'Set price',
    freeItem: 'Free item',
    pricePlaceholder: '0.00',
    invalidPrice: 'Invalid Price',
    pleaseEnterValidPrice: 'Please enter a valid price.',
    
    // Conditions
    brandNew: 'Brand new',
    newWithoutTags: 'New without tags',
    used: 'Used',

    // Step 4: Pickup Preferences
    pickupPreferences: 'Pickup preferences',
    roomPickup: 'Room pickup',
    lobbyPickup: 'Pickup at lobby',
    meetInPerson: 'Meet in person',
    pickupRequired: 'Pickup Required',
    pleaseSelectPickup: 'Please select at least one pickup option.',

    // Step 5: Success
    itemPublished: 'Your item is now published!',
    itemPublishedSubtitle: 'You can find your item and its activity on your marketplace profile page.',
    backToMarketplace: 'Back to marketplace',

    // Errors
    unableToCreate: 'Unable to create listing. Please try again.',
    failedToSubmit: 'Failed to submit listing. Please try again.',
  },

  // ============================================================================
  // Profile Screen
  // ============================================================================
  profile: {
    myProfile: 'My profile',
    listings: 'Listings',
    active: 'Active',
    sold: 'Sold',
    
    // Stats
    bought: 'Bought',
    followers: 'Followers',
    following: 'Following',
    
    // Empty States
    noActiveListings: 'No active listings',
    noItemsSold: 'No items sold yet',
    postItemToStart: 'Post an item to start selling!',
    onceYouSell: 'Once you sell something, it will appear here!',
  },

  // ============================================================================
  // Listing Detail
  // ============================================================================
  listing: {
    condition: 'Condition',
    pickupOptions: 'Pickup Options',
    description: 'Description',
    seller: 'Seller',
    messageToSeller: 'Message to seller',
    messageSeller: 'Message seller',
    contact: 'Contact',
    views: 'views',
    saved: 'saved',
    
    // Actions
    markAsSold: 'Mark as Sold',
    repostItem: 'Repost item',
    editListing: 'Edit listing',
    deleteListing: 'Delete listing',
    report: 'Report',
    share: 'Share',
    
    // Condition Display
    conditionNew: 'New',
    conditionNewWithoutTags: 'New without tags',
    conditionLikeNew: 'Like New',
    conditionUsed: 'Used',
    conditionFair: 'Fair',

    // Pickup Display
    pickupRoom: 'Room pickup',
    pickupLobby: 'Pickup at lobby',
    pickupAny: 'Room pickup • Pickup at lobby • Meet in person',
  },

  // ============================================================================
  // Confirmations
  // ============================================================================
  confirmations: {
    deleteListingTitle: 'Delete listing?',
    deleteListingMessage: 'Are you sure you want to delete this listing? This action cannot be undone.',
    markAsSoldTitle: 'Mark as sold?',
    markAsSoldMessage: 'Are you sure you want to mark this item as sold?',
    markAsSoldConfirmMessage: "Mark this item as sold? You can still find it later in the 'Sold' section.",
    removeListingMessage: 'Remove this item? This action cannot be undone, and the item will be permanently deleted.',
    repostTitle: 'Repost item?',
    repostMessage: 'Do you want to repost this item to make it active again?',
  },

  // ============================================================================
  // Saved Items
  // ============================================================================
  saved: {
    title: 'Saved items',
    noSavedItems: 'No saved items',
    saveItemsToSee: 'Save items to see them here!',
    itemSold: 'Item sold',
  },

  // ============================================================================
  // Following
  // ============================================================================
  following: {
    title: 'Following',
    noFollowing: 'Not following anyone',
    followSellers: 'Follow sellers to see their new listings!',
    newListing: 'New listing',
    follow: 'Follow',
    unfollow: 'Unfollow',
  },

  // ============================================================================
  // Inbox / Messages
  // ============================================================================
  inbox: {
    title: 'Inbox',
    noMessages: 'No messages yet',
    startConversation: 'Start a conversation about a listing!',
    typeMessage: 'Type a message...',
    send: 'Send',
    today: 'Today',
    yesterday: 'Yesterday',
  },

  // ============================================================================
  // Report
  // ============================================================================
  report: {
    title: 'Submit a report',
    subtitle: 'Select a reason for the report, and it will be reviewed by the moderation team.',
    descriptionOptional: 'Description (optionally)',
    
    // Reasons
    spam: 'Spam content',
    inappropriate: 'Inappropriate content',
    harassment: 'Harassment',
    
    // States
    reportSubmitted: 'Report submitted',
    alreadyReported: "You've already reported this content. The moderation team will handle it from here.",
  },

  // ============================================================================
  // Feedback / Reviews
  // ============================================================================
  feedback: {
    title: 'Feedback',
    receivedFeedback: 'Received feedback',
    noFeedback: 'No feedback yet',
    noFeedbackReceived: 'No feedback received yet',
    leaveReview: 'Leave a review',
    rateExperience: 'Rate your experience',
    writeReview: 'Write a review (optional)',
    congratsPurchase: 'Congrats on your purchase! How do you rate the seller?',
    congratsSold: 'Congrats! Who did you sell your item to?',
    reviewOptional: 'Review (optionally)',
    noBuyersFound: 'No buyers found',
    search: 'Search',
  },

  // ============================================================================
  // Seller Profile
  // ============================================================================
  seller: {
    viewProfile: 'View profile',
    ratings: 'ratings',
    noRatings: 'No ratings yet',
    activeListings: 'Active listings',
    noActiveListings: 'No active listings',
  },

  // ============================================================================
  // Navigation / Dropdown
  // ============================================================================
  navigation: {
    myProfile: 'My profile',
    savedItems: 'Saved items',
    inbox: 'Inbox',
    following: 'Following',
  },

  // ============================================================================
  // Recently Viewed
  // ============================================================================
  recentlyViewed: {
    title: 'Recently viewed',
  },

  // ============================================================================
  // Toast Messages
  // ============================================================================
  toast: {
    listingUpdated: 'The listing has been updated!',
    feedbackSubmitted: 'Your feedback has been submitted!',
    itemMarkedSold: 'Item marked as sold!',
    itemPostedAgain: 'Item posted again!',
    itemRemoved: 'Item successfully removed.',
    addedToSaved: 'Added to your saved items!',
    removedFromSaved: 'Removed from your saved items!',
    reportSubmitted: 'Report submitted. It will be reviewed by the moderation team.',
    feedbackFailed: 'Failed to submit feedback. Please try again.',
    markSoldFailed: 'Failed to mark as sold. Please try again.',
    repostFailed: 'Failed to repost listing. Please try again.',
    removeFailed: 'Failed to remove listing. Please try again.',
  },

  // ============================================================================
  // Errors & Alerts
  // ============================================================================
  errors: {
    failedToLoad: 'Failed to load',
    tryAgain: 'Please try again',
    networkError: 'Network error. Please check your connection.',
    sessionExpired: 'Session expired. Please log in again.',
  },

  // ============================================================================
  // Accessibility Labels
  // ============================================================================
  accessibility: {
    closeButton: 'Close',
    backButton: 'Go back',
    saveButton: 'Save item',
    shareButton: 'Share listing',
    reportButton: 'Report listing',
    photoGallery: 'Photo gallery',
    nextPhoto: 'Next photo',
    previousPhoto: 'Previous photo',
    rating: (count: number) => `${count} out of 5 stars`,
  },
} as const;

// Type exports for type safety
export type MarketplaceStrings = typeof marketplaceStrings;

// Helper function for dynamic strings
export const formatMarketplaceString = (template: string, ...args: (string | number)[]): string => {
  let result = template;
  args.forEach((arg, index) => {
    result = result.replace(`{${index}}`, String(arg));
  });
  return result;
};

// Export individual sections for easier imports
export const commonStrings = marketplaceStrings.common;
export const postItemStrings = marketplaceStrings.postItem;
export const profileStrings = marketplaceStrings.profile;
export const listingStrings = marketplaceStrings.listing;
export const confirmationStrings = marketplaceStrings.confirmations;
export const savedStrings = marketplaceStrings.saved;
export const followingStrings = marketplaceStrings.following;
export const inboxStrings = marketplaceStrings.inbox;
export const reportStrings = marketplaceStrings.report;
export const feedbackStrings = marketplaceStrings.feedback;
export const sellerStrings = marketplaceStrings.seller;
export const navigationStrings = marketplaceStrings.navigation;
export const toastStrings = marketplaceStrings.toast;
export const errorStrings = marketplaceStrings.errors;
